import { useEffect } from "react";
import { usePrecisionGameStore } from "../store/precisionGame.store";
import { usePrecisionGameLeaderboard } from "./usePrecisionGameLeaderboard";

export const usePrecisionGameSync = (
  collection: string,
  currentUser: any,
  attemptCount: number,
  setSuccess: (value: boolean) => void
) => {
  const {
    addWonCollection,
    hasWonCollection,
    setAttemptCount,
    removeWonCollection,
  } = usePrecisionGameStore();

  const { winners, mentions, loading, refetch } =
    usePrecisionGameLeaderboard(collection);

  // Synchronize local and server state for winning status
  useEffect(() => {
    if (!currentUser?.uid || loading) return;

    // Only proceed when we have data and it's not loading
    if (winners) {
      const userWinEntry = winners.find(
        (winner) => winner.uid === currentUser.uid
      );

      // CASE 1: User has won on server but not in local store
      if (userWinEntry && !hasWonCollection(collection)) {
        // Update local state to match server state
        addWonCollection(collection);
        setSuccess(true);

        // Also sync the attempt count if server has more attempts recorded
        if (userWinEntry.attempts && userWinEntry.attempts > attemptCount) {
          setAttemptCount(collection, userWinEntry.attempts);
        }
      }
      // CASE 2: User has won in local store but not on server
      else if (!userWinEntry && hasWonCollection(collection)) {
        // Remove the collection from won collections locally
        removeWonCollection(collection);
        setSuccess(false);
        setAttemptCount(collection, 0); // Reset attempt count locally
      }
      // CASE 3: If user hasn't won, check mentions for attempt count synchronization
      else if (!userWinEntry && mentions && mentions.length > 0) {
        // Find all mentions by this user
        const userMentions = mentions.filter(
          (mention) => mention.uid === currentUser.uid
        );

        if (userMentions.length > 0) {
          // Find the highest attempt count from the user's mentions
          const highestAttemptCount = Math.max(
            ...userMentions.map((mention) => mention.attempts || 0)
          );

          // Update local attempt count if server has a higher value
          if (highestAttemptCount > attemptCount) {
            setAttemptCount(collection, highestAttemptCount);
          }
        }
      }
    }
  }, [
    winners,
    mentions,
    currentUser?.uid,
    collection,
    hasWonCollection,
    loading,
    attemptCount,
  ]);

  return {
    winners,
    mentions,
    loading,
    refetch,
  };
};
